import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        <code>gltf-transform optimize input.glb output.glb</code> runs a full optimization pipeline in one command: it
        removes duplicate data, instances repeated meshes, flattens and joins the scene to cut draw calls, welds and
        lightly simplifies geometry, prunes unused data, caps textures at 2048 px and compresses geometry with Meshopt.
        For the smallest files on the web, the usual recipe is{' '}
        <code>--compress draco --texture-compress webp</code>, plus <code>--texture-size 1024</code> when your textures
        don't need 2K.
      </p>
      <p>
        This guide covers installation, what each step does, every <code>optimize</code> flag with its default, the
        individual commands for finer control, the scripting API and the errors people hit most. Everything here was
        checked against <code>@gltf-transform/cli</code> 4.5.0.
      </p>

      <h2>Install gltf-transform</h2>
      <p>You can run the CLI without installing anything through npx, or install it globally:</p>
      <CodeBlock language="bash">{`# Run once without installing
npx @gltf-transform/cli optimize input.glb output.glb

# Or install globally, then use the gltf-transform command
npm install --global @gltf-transform/cli
gltf-transform --version`}</CodeBlock>
      <ul>
        <li><strong>Node.js 20 or newer</strong> is required.</li>
        <li>
          <strong>sharp</strong>, the image library used for resizing and WebP, AVIF, JPEG and PNG encoding, is
          installed automatically as a dependency of the CLI.
        </li>
        <li>
          <strong>KTX2</strong> texture compression is the exception: it calls the <code>ktx</code> tool from{' '}
          <a href="https://github.com/KhronosGroup/KTX-Software" target="_blank" rel="noopener noreferrer">KTX-Software</a>{' '}
          (version 4.4.0 or newer), which you install separately.
        </li>
      </ul>

      <h2>What gltf-transform optimize does, step by step</h2>
      <p>
        The <code>optimize</code> command chains existing gltf-transform functions in this order. Each step can be
        switched off with its flag, except the first.
      </p>
      <ol>
        <li><strong>dedup</strong>: merges duplicate accessors, meshes, materials, textures and skins.</li>
        <li><strong>instance</strong>: turns a mesh reused 5 or more times into GPU instances (<code>EXT_mesh_gpu_instancing</code>).</li>
        <li><strong>palette</strong>: packs solid-color material values into small palette textures and merges the materials, which helps CAD and low-poly scenes.</li>
        <li><strong>flatten</strong>: flattens the node hierarchy.</li>
        <li><strong>join</strong>: joins meshes that share a material to reduce draw calls.</li>
        <li><strong>weld</strong>: merges identical vertices (required for simplification and Draco).</li>
        <li><strong>simplify</strong>: reduces triangles with meshoptimizer, using a very small default error of 0.0001 (0.01% of the mesh size).</li>
        <li><strong>resample</strong>: removes redundant animation keyframes without changing the motion.</li>
        <li><strong>prune</strong>: removes anything not used by a scene, including unused vertex attributes, and turns single-color textures into material factors.</li>
        <li><strong>sparse</strong>: stores mostly-zero arrays (common in morph targets) as sparse accessors.</li>
        <li><strong>texture compression</strong>: resizes textures to the maximum size and re-encodes them.</li>
        <li><strong>geometry compression</strong>: Meshopt by default, or Draco or quantization.</li>
      </ol>
      <p>
        To see where the weight is before and after, run <code>gltf-transform inspect model.glb</code>. It lists every
        mesh, material and texture with sizes and resolutions, and the extensions the file requires.
      </p>

      <h2>optimize flags and defaults</h2>
      <p>
        From <code>gltf-transform optimize --help</code>. Boolean flags take <code>true</code> or <code>false</code>{' '}
        (<code>--simplify false</code>); the <code>--no-simplify</code> form also works.
      </p>
      <table>
        <thead>
          <tr><th>Flag</th><th>Default</th><th>What it does</th></tr>
        </thead>
        <tbody>
          <tr><td><code>--compress</code></td><td>meshopt</td><td>Geometry compression: <code>draco</code>, <code>meshopt</code>, <code>quantize</code> or <code>false</code></td></tr>
          <tr><td><code>--meshopt-level</code></td><td>high</td><td><code>medium</code> or <code>high</code></td></tr>
          <tr><td><code>--texture-compress</code></td><td>auto</td><td><code>webp</code>, <code>avif</code>, <code>ktx2</code>, <code>auto</code> (re-encode in the original format) or <code>false</code></td></tr>
          <tr><td><code>--texture-size</code></td><td>2048</td><td>Maximum texture width and height in pixels; aspect ratio is kept</td></tr>
          <tr><td><code>--simplify</code></td><td>true</td><td>Simplify meshes with meshoptimizer</td></tr>
          <tr><td><code>--simplify-error</code></td><td>0.0001</td><td>Error tolerance as a fraction of the mesh extent</td></tr>
          <tr><td><code>--simplify-ratio</code></td><td>0</td><td>Target fraction of vertices to keep (0 to 1)</td></tr>
          <tr><td><code>--simplify-lock-border</code></td><td>false</td><td>Lock topological borders while simplifying</td></tr>
          <tr><td><code>--instance</code> / <code>--instance-min</code></td><td>true / 5</td><td>GPU instancing and the number of copies needed</td></tr>
          <tr><td><code>--palette</code> / <code>--palette-min</code></td><td>true / 5</td><td>Palette textures and the minimum number of unique values</td></tr>
          <tr><td><code>--flatten</code></td><td>true</td><td>Flatten the scene graph</td></tr>
          <tr><td><code>--join</code></td><td>true</td><td>Join meshes to reduce draw calls</td></tr>
          <tr><td><code>--join-meshes</code> / <code>--join-named</code></td><td>true / true</td><td>Whether distinct meshes and named meshes and nodes may be joined</td></tr>
          <tr><td><code>--weld</code></td><td>true</td><td>Merge equivalent vertices</td></tr>
          <tr><td><code>--resample</code></td><td>true</td><td>Losslessly deduplicate animation keyframes</td></tr>
          <tr><td><code>--prune</code></td><td>true</td><td>Remove unreferenced data (see also <code>--prune-attributes</code>, <code>--prune-solid-textures</code>)</td></tr>
          <tr><td><code>--sparse</code></td><td>true</td><td>Sparse storage for zero-filled arrays</td></tr>
        </tbody>
      </table>
      <Callout title="The default geometry compression is Meshopt, not Draco">
        A plain <code>optimize</code> run writes <code>EXT_meshopt_compression</code>, so your viewer needs a Meshopt
        decoder. If your platform only supports Draco, or you don't control the viewer, pass{' '}
        <code>--compress draco</code>, <code>--compress quantize</code> or <code>--compress false</code>.
      </Callout>

      <h2>Practical optimize examples</h2>
      <h3>Smallest file for three.js or &lt;model-viewer&gt;</h3>
      <CodeBlock language="bash">{`gltf-transform optimize input.glb output.glb \\
  --compress draco \\
  --texture-compress webp \\
  --texture-size 1024`}</CodeBlock>
      <p>
        Here is what different settings did to the Khronos DamagedHelmet sample (3.77 MB, five 2048 px JPEG textures)
        with CLI 4.5.0. It is one texture-heavy model, so treat the numbers as an illustration, not a promise:
      </p>
      <table>
        <thead>
          <tr><th>Command</th><th>Output size</th></tr>
        </thead>
        <tbody>
          <tr><td><code>optimize</code> (all defaults)</td><td>2.62 MB</td></tr>
          <tr><td><code>--texture-compress webp</code></td><td>1.44 MB</td></tr>
          <tr><td><code>--compress draco --texture-compress webp</code></td><td>1.34 MB</td></tr>
          <tr><td><code>--compress draco --texture-compress webp --texture-size 1024</code></td><td>442 KB</td></tr>
          <tr><td><code>--compress draco --texture-compress avif --texture-size 1024</code></td><td>288 KB</td></tr>
        </tbody>
      </table>
      <p>
        On this model the texture settings mattered far more than the geometry codec, which is typical for PBR assets.
        For geometry-heavy files such as scans or CAD, the <code>--compress</code> choice matters more. See{' '}
        <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt</Link> for how to pick.
      </p>

      <h3>Keep the scene structure intact</h3>
      <p>
        Flattening and joining are great for draw calls but they remove the hierarchy and merge nodes. If your app finds
        nodes by name, animates individual parts or toggles visibility per mesh, turn them off:
      </p>
      <CodeBlock language="bash">{`# Keep hierarchy and separate meshes
gltf-transform optimize input.glb output.glb --flatten false --join false

# Or still join, but leave named meshes and nodes alone
gltf-transform optimize input.glb output.glb --join-named false`}</CodeBlock>

      <h3>Control simplification</h3>
      <p>
        The default simplification is conservative. To cut triangles harder, set a ratio and a larger error limit. To
        keep geometry exactly as modeled (CAD, measured scans), switch it off:
      </p>
      <CodeBlock language="bash">{`# Aim for 50% of vertices, allowing up to 0.1% error
gltf-transform optimize input.glb output.glb --simplify-ratio 0.5 --simplify-error 0.001

# No simplification at all
gltf-transform optimize input.glb output.glb --simplify false`}</CodeBlock>
      <p>
        The simplifier stops early when it hits the error limit, so the ratio is a target, not a guarantee. If you see
        cracks along seams, add <code>--simplify-lock-border true</code> or lower the error.
      </p>

      <h3>Leave textures alone, or use KTX2</h3>
      <p>
        <code>--texture-compress false</code> skips the texture step entirely, and with it the <code>--texture-size</code>{' '}
        resize. <code>--texture-compress ktx2</code> produces GPU-compressed KTX2 textures (UASTC for normal, occlusion
        and metal/roughness maps, ETC1S for the rest). The CLI's own help sums up the trade-off: KTX2 optimizes GPU
        memory and performance, while WebP and AVIF optimize download size. KTX2 also needs KTX-Software installed.
      </p>

      <h3>Maximum viewer compatibility</h3>
      <p>
        Draco and Meshopt both need a decoder in the viewer. <code>--compress false</code> writes plain geometry any
        glTF viewer can read. <code>--compress quantize</code> sits in between: it stores smaller integer attributes via{' '}
        <code>KHR_mesh_quantization</code> with no decoder library, but the loader still has to support that extension.
      </p>

      <h2>Individual commands for finer control</h2>
      <p>
        Every step of <code>optimize</code> is also its own command, with more options. Run{' '}
        <code>gltf-transform &lt;command&gt; --help</code> for the full list. The most useful:
      </p>
      <ul>
        <li><code>inspect</code>: sizes, resolutions and extensions (<code>--format md</code> or <code>csv</code> for reports).</li>
        <li><code>validate</code>: runs the Khronos glTF validator.</li>
        <li><code>dedup</code>, <code>prune</code>, <code>weld</code>, <code>simplify</code> (<code>--ratio</code>, <code>--error</code>), <code>flatten</code>, <code>join</code>, <code>instance</code>.</li>
        <li><code>resize</code>: <code>--width</code> and <code>--height</code> as maximums; textures are never enlarged.</li>
        <li><code>webp</code>, <code>avif</code>, <code>jpeg</code>, <code>png</code>: re-encode with <code>--quality</code>, filtered by <code>--slots</code> (for example <code>"baseColorTexture"</code>).</li>
        <li><code>etc1s</code> and <code>uastc</code>: KTX2 + Basis Universal texture compression.</li>
        <li><code>draco</code>: <code>--quantize-position</code> (default 14 bits), <code>--quantize-normal</code> (10), <code>--quantize-texcoord</code> (12), <code>--encode-speed</code> and <code>--decode-speed</code> (5).</li>
        <li><code>meshopt</code> (<code>--level medium|high</code>) and <code>quantize</code>.</li>
      </ul>
      <p>A manual pipeline on the same helmet looked like this:</p>
      <CodeBlock language="bash">{`gltf-transform resize input.glb step1.glb --width 1024 --height 1024   # 3.77 MB → 1.25 MB
gltf-transform webp step1.glb step2.glb --quality 80                  # → 928 KB
gltf-transform draco step2.glb output.glb                             # → 448 KB`}</CodeBlock>
      <p>
        The CLI reads and writes both formats: give the output a <code>.gltf</code> extension to get JSON with separate
        .bin and image files, or <code>.glb</code> for a single binary.
      </p>

      <h2>Scripting with @gltf-transform/core and functions</h2>
      <p>
        For build pipelines, use the same functions from Node.js. Install the packages plus the encoders you need:
      </p>
      <CodeBlock language="bash">{`npm install @gltf-transform/core @gltf-transform/extensions @gltf-transform/functions draco3dgltf meshoptimizer sharp`}</CodeBlock>
      <CodeBlock language="javascript">{`import { NodeIO } from '@gltf-transform/core';
import { ALL_EXTENSIONS } from '@gltf-transform/extensions';
import { dedup, prune, weld, simplify, textureCompress, draco } from '@gltf-transform/functions';
import { MeshoptSimplifier } from 'meshoptimizer';
import draco3d from 'draco3dgltf';
import sharp from 'sharp';

// Register extensions and the Draco encoder/decoder modules.
const io = new NodeIO()
  .registerExtensions(ALL_EXTENSIONS)
  .registerDependencies({
    'draco3d.decoder': await draco3d.createDecoderModule(),
    'draco3d.encoder': await draco3d.createEncoderModule(),
  });

await MeshoptSimplifier.ready;

const document = await io.read('input.glb');

await document.transform(
  dedup(),
  prune(),
  weld(),
  simplify({ simplifier: MeshoptSimplifier, ratio: 0.75, error: 0.001 }),
  textureCompress({ encoder: sharp, targetFormat: 'webp', resize: [1024, 1024], quality: 80 }),
  draco(),
);

await io.write('output.glb', document);`}</CodeBlock>
      <p>
        Save it as an <code>.mjs</code> file (or set <code>"type": "module"</code>) so top-level <code>await</code>{' '}
        works. Two details trip people up: <code>draco()</code> needs the <code>draco3dgltf</code> modules registered on
        the IO, and <code>textureCompress()</code> needs sharp passed as <code>encoder</code>. Without it, a fallback is
        used and most quality options are ignored. To use Meshopt instead of Draco, await{' '}
        <code>MeshoptEncoder.ready</code>, register it on the IO as <code>'meshopt.encoder'</code> (and{' '}
        <code>MeshoptDecoder</code> as <code>'meshopt.decoder'</code>), then call{' '}
        <code>meshopt({'{'} encoder: MeshoptEncoder {'}'})</code>. Skipping the registration fails at write time.
      </p>

      <h2>Common problems</h2>
      <h3>KTX2 fails with "Command failed: command -v ktx"</h3>
      <p>
        You asked for KTX2 (<code>--texture-compress ktx2</code>, <code>etc1s</code> or <code>uastc</code>) without
        KTX-Software on your PATH. Depending on the platform, the error may instead read{' '}
        <code>Command "ktx" not found</code>. Install KTX-Software 4.4.0 or newer and check that <code>ktx --version</code> works in
        the same terminal.
      </p>
      <h3>sharp fails to install or load</h3>
      <p>
        sharp ships prebuilt binaries per platform, installed as optional dependencies. Use Node.js 20.9 or newer, don't
        disable optional dependencies, and reinstall on the machine that runs the code: a <code>node_modules</code>{' '}
        copied from macOS won't work on Linux. For cross-platform builds, the{' '}
        <a href="https://sharp.pixelplumbing.com/install" target="_blank" rel="noopener noreferrer">sharp install docs</a>{' '}
        explain flags like <code>npm install --cpu=x64 --os=linux --libc=glibc sharp</code>. Don't bundle sharp with
        webpack, Vite or esbuild; mark it as external.
      </p>
      <h3>The optimized model doesn't show up in the browser</h3>
      <p>
        The file now requires a decoder. In three.js you'll see errors like{' '}
        <code>THREE.GLTFLoader: No DRACOLoader instance provided</code> or{' '}
        <code>setMeshoptDecoder must be called before loading compressed files</code>. Attach a{' '}
        <code>DRACOLoader</code>, <code>MeshoptDecoder</code> or <code>KTX2Loader</code> to your <code>GLTFLoader</code>,
        as shown in <Link to="/blog/threejs-load-glb">how to load GLB files in three.js</Link>.
      </p>
      <h3>Parts of the model moved, merged or lost their names</h3>
      <p>
        That is <code>flatten</code> and <code>join</code> at work. Re-run with <code>--flatten false --join false</code>{' '}
        or <code>--join-named false</code>. Run the result through the <Link to="/gltf-validator">glTF validator</Link> to
        confirm the file is still valid.
      </p>

      <h2>When a hosted optimizer is easier</h2>
      <p>
        The CLI is the right tool if you're comfortable with Node.js and want Meshopt or KTX2. If you'd rather not
        install Node, sharp and KTX-Software on every machine, or you need non-developers to compress files,{' '}
        <Link to="/">compress-glb.com</Link> runs on gltf-transform on our servers. It covers Draco, texture resizing,
        WebP/AVIF/JPEG/PNG re-encoding and geometry passes such as weld, simplify, instancing, flatten and merge meshes,
        with saved presets, batch uploads and a{' '}
        <Link to="/docs/api">REST API</Link> for pipelines. It doesn't offer Meshopt or KTX2 output; for those, stick
        with the CLI.
      </p>

      <h2>FAQ</h2>
      <h3>What does gltf-transform optimize do by default?</h3>
      <p>
        It runs dedup, instance, palette, flatten, join, weld, simplify, resample, prune and sparse, re-encodes textures
        in their original format at up to 2048 px, and compresses geometry with Meshopt. Every step except dedup can be
        turned off with its flag.
      </p>
      <h3>How do I set the texture size in gltf-transform optimize?</h3>
      <p>
        Use <code>--texture-size 1024</code> (or any pixel value). It is a maximum for width and height, the aspect ratio
        is kept, and it only applies when <code>--texture-compress</code> isn't <code>false</code>.
      </p>
      <h3>Should I use Draco or Meshopt with gltf-transform?</h3>
      <p>
        Draco usually gives smaller static meshes; Meshopt decodes faster and also compresses animation and morph
        targets. Pick whichever your viewer supports. The <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt</Link>{' '}
        comparison goes deeper.
      </p>
      <h3>Does gltf-transform optimize reduce quality?</h3>
      <p>
        Some steps are lossy: simplification, quantization in Draco and Meshopt, and texture re-encoding and resizing.
        The defaults are conservative (a simplification error of 0.0001, for example), but compare the output with
        the original side by side before shipping.
      </p>
      <h3>Is gltf-transform free?</h3>
      <p>
        Yes. glTF Transform is open source under the MIT license, and the CLI and libraries are free to use in
        commercial projects.
      </p>
    </>
  );
}
